/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import {
    entitySwaggerToEntityStore,
    simpleEntityStoreToSimpleEntitySwagger,
    simpleEntityToSimpleEntitySwagger
} from 'src/luis/core/services/entities/mappers';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { EntitySwagger } from 'src/luis/projects/entities/types/swagger/EntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

export type SimpleEntityWithChildrenInput = {
    name: string;
    children: SimpleEntityWithChildrenInput[];
    instanceOf?: LuisId;
};

/**
 * Represents a service to perform CRUD operations for simple entity.
 */
export const getSimpleEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param entityId An optional entity id to add.
     * @returns A url string to call.
     */
    const getUrl = (entityId?: LuisId): string =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('entities', `${entityId || ''}`)
            .build();

    /**
     * Gets the entity with provided id.
     */
    const getEntity = async ({ id }: Pick<SimpleEntityStore, 'id'>): Promise<SimpleEntityStore> => {
        const { data: entity } = await httpService.get<EntitySwagger>(getUrl(id), apiOptions);

        return <SimpleEntityStore>entitySwaggerToEntityStore(entity);
    };

    /**
     * Creates a new simple entity.
     *
     * @param name The new simple entity name.
     *
     * @returns A new simple entity store.
     */
    const createEntity = async ({ name, children }: Pick<SimpleEntity, 'name' | 'children'>): Promise<SimpleEntityStore> => {
        const updatedChildren = children ? children.map(simpleEntityToSimpleEntitySwagger) : null;

        const { data: entityId } = await httpService.post<string>(getUrl(), { name, children: updatedChildren }, apiOptions);

        return getEntity({ id: entityId });
    };

    /**
     * Creates a new simple entity (from an EntityStore).
     *
     * @param name The new simple entity name.
     *
     * @returns A new simple entity store.
     */
    const createEntityV2 = async ({ name, children }: Pick<SimpleEntityStore, 'name' | 'children'>): Promise<SimpleEntityStore> => {
        const updatedChildren = children ? children.map(simpleEntityStoreToSimpleEntitySwagger) : null;

        const { data: entityId } = await httpService.post<string>(getUrl(), { name, children: updatedChildren }, apiOptions);

        return getEntity({ id: entityId });
    };

    /**
     * Creates a new simple child entity.
     *
     * @param name The new simple child entity name.
     * @param children The new simple child entity children.
     * @param parentId The new simple child entity's parent id.
     *
     * @returns A new simple entity store.
     */
    const createNodeChildEntity = async ({
        name,
        children,
        parentId
    }: SimpleEntityWithChildrenInput & { parentId: LuisId }): Promise<SimpleEntityStore> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('entities', `${parentId}`)
            .resource('children')
            .build();

        const { data: newChildId } = await httpService.post<string>(url, { name, children }, apiOptions);

        return getEntity({ id: newChildId });
    };

    /**
     * Creates a new simple child entity.
     *
     * @param name The new simple child entity name.
     * @param instanceOf The new simple child entity instanceOf field (non-ML dependency).
     * @param parentId The new simple child entity's parent id.
     *
     * @returns A new simple entity store.
     */
    const createLeafChildEntity = async ({
        name,
        instanceOf,
        parentId,
        absoluteParentId
    }: Pick<SimpleEntityStore, 'name' | 'instanceOf'> & { parentId: LuisId; absoluteParentId: LuisId }): Promise<SimpleEntityStore> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('entities', `${parentId}`)
            .resource('children')
            .build();

        await httpService.post<string>(url, { name, instanceOf }, apiOptions);

        return getEntity({ id: absoluteParentId });
    };

    /**
     * Deletes the given simple entity.
     *
     * @param id The id of the simple entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<SimpleEntityStore, 'id'>): Promise<void> => {
        await httpService.delete(getUrl(id), apiOptions);
    };

    /**
     * Renames the given simple entity.
     *
     * @param id The id of the simple entity to rename.
     * @param name The simple entity new name.
     */
    const renameEntity = async ({ id, name }: Partial<SimpleEntityStore>): Promise<void> => {
        await httpService.patch(getUrl(id), { name }, apiOptions);
    };

    /**
     * Updates the given simple child entity's instanceOf field.
     *
     * @param id The id of the simple child entity to update.
     * @param instanceOf The entity name.
     */
    const updateChildEntity = async ({ id, instanceOf }: Partial<SimpleEntityStore>): Promise<void> => {
        await httpService.patch(getUrl(id), { instanceOf }, apiOptions);
    };

    return {
        getEntity,
        createEntity,
        createEntityV2,
        createNodeChildEntity,
        createLeafChildEntity,
        renameEntity,
        deleteEntity,
        updateChildEntity
    };
});
